/**
 * Three.js vault corridor background.
 *
 * Renders a cinematic vault-corridor scene matching the Gringotts landing page 1:1:
 * combination lock dials, keyhole core with FBM shader, multi-layer particles
 * with SDF shapes, corridor rings with noise displacement, and cinematic
 * post-processing (bloom, vignette, chromatic aberration, film grain).
 */
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

interface VaultSceneProps {
  className?: string;
  /** Whether the scene should animate (false = render once). */
  active?: boolean;
  /** Deployment progress 0-1 for bloom intensity ramp. */
  progress?: number;
}

// ---------------------------------------------------------------------------
// Corridor ring data (from landing page)
// ---------------------------------------------------------------------------

const ALL_RING_DATA = [
  { z: 0, r: 7.5, tube: 0.018, tilt: 0, opacity: 0.3 },
  { z: -4, r: 6.8, tube: 0.014, tilt: 0.04, opacity: 0.24 },
  { z: -8, r: 6.2, tube: 0.012, tilt: -0.03, opacity: 0.18 },
  { z: -13, r: 5.5, tube: 0.01, tilt: 0.06, opacity: 0.13 },
  { z: -18, r: 4.8, tube: 0.008, tilt: -0.05, opacity: 0.09 },
  { z: -24, r: 4.0, tube: 0.006, tilt: 0.04, opacity: 0.06 },
  { z: -30, r: 3.2, tube: 0.005, tilt: -0.03, opacity: 0.04 },
  { z: -38, r: 2.4, tube: 0.004, tilt: 0.02, opacity: 0.025 },
] as const;

// ---------------------------------------------------------------------------
// Combination lock dial data (from landing page)
// ---------------------------------------------------------------------------

const DIAL_DATA = [
  { r: 1.2, tube: 0.03, ticks: 12, speed: 0.15, opacity: 0.4, z: 0.15 },
  { r: 1.9, tube: 0.022, ticks: 20, speed: -0.09, opacity: 0.28, z: 0.0 },
  { r: 2.8, tube: 0.015, ticks: 32, speed: 0.05, opacity: 0.18, z: -0.15 },
] as const;

export function VaultScene({
  className,
  active = true,
  progress = 0,
}: VaultSceneProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<{
    renderer: THREE.WebGLRenderer;
    composer: EffectComposer;
    bloom: UnrealBloomPass;
    camera: THREE.PerspectiveCamera;
    scene: THREE.Scene;
    ringGroup: THREE.Group;
    ringMeshes: { mesh: THREE.Mesh; speed: number; baseTilt: number }[];
    ringMaterials: THREE.ShaderMaterial[];
    gyroGroup: THREE.Group;
    dialRings: { group: THREE.Group; speed: number }[];
    dialMaterials: THREE.ShaderMaterial[];
    keyholeQuad: THREE.Mesh;
    keyholeMat: THREE.ShaderMaterial;
    farParticles: THREE.Points;
    farMat: THREE.ShaderMaterial;
    nearParticles: THREE.Points | null;
    dustMotes: THREE.Points;
    dustMat: THREE.ShaderMaterial;
    lantern: THREE.PointLight;
    cinematicPass: ShaderPass;
    frameId: number;
    frame: number;
    lastRender: number;
    targetX: number;
    targetY: number;
    mouseX: number;
    mouseY: number;
    reducedMotion: boolean;
    disposables: (THREE.BufferGeometry | THREE.Material | THREE.Texture)[];
    isDesktop: boolean;
  } | null>(null);

  const progressRef = useRef(progress);
  progressRef.current = progress;

  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const w = container.clientWidth;
    const h = container.clientHeight;
    const isDesktop = window.innerWidth > 768;
    const dpr = Math.min(window.devicePixelRatio, isDesktop ? 2 : 1.5);

    const disposables: (
      | THREE.BufferGeometry
      | THREE.Material
      | THREE.Texture
    )[] = [];

    // -----------------------------------------------------------------------
    // Renderer (matching landing page exactly)
    // -----------------------------------------------------------------------
    const renderer = new THREE.WebGLRenderer({
      antialias: isDesktop,
      alpha: false,
    });
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h);
    renderer.setClearColor(0x0a0a0a, 1);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace; // Match r128 — no auto sRGB brightening
    renderer.shadowMap.enabled = false;
    container.appendChild(renderer.domElement);

    // -----------------------------------------------------------------------
    // Scene
    // -----------------------------------------------------------------------
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x050401, 0.022);

    // -----------------------------------------------------------------------
    // Camera
    // -----------------------------------------------------------------------
    const camera = new THREE.PerspectiveCamera(52, w / h, 0.1, 120);
    camera.position.set(0, 0, 18);
    camera.lookAt(0, 0, 0);

    // -----------------------------------------------------------------------
    // Lights
    // -----------------------------------------------------------------------
    const hemi = new THREE.HemisphereLight(0x1a1008, 0x020208, 3);
    scene.add(hemi);

    // Gold lantern with flicker
    const lantern = new THREE.PointLight(0xc9a84c, 4.5, 35);
    lantern.position.set(0, 3, 14);
    scene.add(lantern);

    // Deep corridor light
    const deepLight = new THREE.PointLight(0x7a4c10, 1.8, 60);
    deepLight.position.set(0, 0, -20);
    scene.add(deepLight);

    // -----------------------------------------------------------------------
    // Shared vertex shaders
    // -----------------------------------------------------------------------
    const fresnelVertexShader = /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vViewDir;
      void main() {
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vNormal = normalize(normalMatrix * normal);
        vViewDir = normalize(-mvPosition.xyz);
        gl_Position = projectionMatrix * mvPosition;
      }
    `;

    // Ring displacement vertex shader (desktop only) — simple hash noise
    const ringDisplacementVertexShader = /* glsl */ `
      uniform float uTubeRadius;
      uniform float uTime;
      varying vec3 vNormal;
      varying vec3 vViewDir;
      float hash(vec3 p) {
        p = fract(p * 0.3183099 + vec3(0.1));
        p *= 17.0;
        return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
      }
      float noise3(vec3 p) {
        vec3 i = floor(p);
        vec3 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(
          mix(mix(hash(i), hash(i + vec3(1,0,0)), f.x),
              mix(hash(i + vec3(0,1,0)), hash(i + vec3(1,1,0)), f.x), f.y),
          mix(mix(hash(i + vec3(0,0,1)), hash(i + vec3(1,0,1)), f.x),
              mix(hash(i + vec3(0,1,1)), hash(i + vec3(1,1,1)), f.x), f.y), f.z
        );
      }
      void main() {
        float disp = noise3(position * 3.0 + uTime * 0.3) * uTubeRadius * 0.5;
        vec3 newPos = position + normal * disp;
        vec4 mvPosition = modelViewMatrix * vec4(newPos, 1.0);
        vNormal = normalize(normalMatrix * normal);
        vViewDir = normalize(-mvPosition.xyz);
        gl_Position = projectionMatrix * mvPosition;
      }
    `;

    // Ring fragment shader (matches landing)
    const ringFragmentShader = /* glsl */ `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uTime;
      uniform float uRingIndex;
      varying vec3 vNormal;
      varying vec3 vViewDir;
      void main() {
        float fresnel = pow(1.0 - abs(dot(vNormal, vViewDir)), 2.5);
        float pulse = 0.85 + 0.15 * sin(uTime * 0.5 + uRingIndex * 1.2);
        vec3 col = uColor * 1.8 * fresnel * pulse;
        float alpha = uOpacity * fresnel * pulse;
        gl_FragColor = vec4(col, alpha);
      }
    `;

    // -----------------------------------------------------------------------
    // Corridor rings
    // -----------------------------------------------------------------------
    const ringGroup = new THREE.Group();
    const corridorRingData = isDesktop
      ? ALL_RING_DATA
      : ALL_RING_DATA.slice(0, 5);
    const torusSegs = isDesktop ? 200 : 100;

    const ringMeshes: { mesh: THREE.Mesh; speed: number; baseTilt: number }[] =
      [];
    const ringMaterials: THREE.ShaderMaterial[] = [];

    corridorRingData.forEach((d, i) => {
      const geo = new THREE.TorusGeometry(
        d.r,
        d.tube,
        isDesktop ? 24 : 8,
        torusSegs,
      );
      disposables.push(geo);

      const uniforms: Record<string, THREE.IUniform> = {
        uColor: { value: new THREE.Color(0.78, 0.66, 0.3) },
        uOpacity: { value: d.opacity },
        uTime: { value: 0 },
        uRingIndex: { value: i },
        uTubeRadius: { value: d.tube },
      };

      const mat = new THREE.ShaderMaterial({
        vertexShader: isDesktop
          ? ringDisplacementVertexShader
          : fresnelVertexShader,
        fragmentShader: ringFragmentShader,
        uniforms,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      disposables.push(mat);

      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.z = d.z;
      mesh.rotation.x = d.tilt;
      mesh.rotation.y = (i % 2 === 0 ? 1 : -1) * 0.02;

      const speed = (0.0003 + i * 0.00008) * (i % 2 === 0 ? 1 : -1);
      ringMeshes.push({ mesh, speed, baseTilt: d.tilt });
      ringMaterials.push(mat);
      ringGroup.add(mesh);
    });

    // Accent rings (desktop only) — midpoints between ring pairs
    if (isDesktop) {
      for (let i = 0; i < ALL_RING_DATA.length - 1; i++) {
        const a = ALL_RING_DATA[i];
        const b = ALL_RING_DATA[i + 1];
        if (!a || !b) continue;
        const geo = new THREE.TorusGeometry((a.r + b.r) / 2, 0.003, 4, 200);
        disposables.push(geo);
        const mat = new THREE.MeshBasicMaterial({
          color: 0xc9a84c,
          transparent: true,
          opacity: ((a.opacity + b.opacity) / 2) * 0.3,
        });
        disposables.push(mat);
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.z = (a.z + b.z) / 2;
        ringGroup.add(mesh);
      }
    }

    scene.add(ringGroup);

    // -----------------------------------------------------------------------
    // Vault lock mechanism (gyroGroup)
    // -----------------------------------------------------------------------
    const gyroGroup = new THREE.Group();
    gyroGroup.position.set(0, 0, 4);

    // Dial ring vertex shader — passes object position for tick marks
    const dialVertexShader = /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vViewDir;
      varying vec3 vObjPos;
      void main() {
        vObjPos = position;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vNormal = normalize(normalMatrix * normal);
        vViewDir = normalize(-mvPosition.xyz);
        gl_Position = projectionMatrix * mvPosition;
      }
    `;

    // Dial fragment shader with time/pulse/tick pattern
    const dialFragmentShader = /* glsl */ `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uTime;
      uniform float uTicks;
      varying vec3 vNormal;
      varying vec3 vViewDir;
      varying vec3 vObjPos;
      void main() {
        float fresnel = pow(1.0 - abs(dot(vNormal, vViewDir)), 2.0);
        float pulse = 0.85 + 0.15 * sin(uTime * 0.4);
        float angle = atan(vObjPos.y, vObjPos.x);
        float tickPattern = smoothstep(0.92, 1.0, cos(angle * uTicks));
        float tickBright = 1.0 + tickPattern * 2.0;
        vec3 col = uColor * 1.8 * fresnel * pulse * tickBright;
        float alpha = uOpacity * fresnel * pulse * (0.6 + tickPattern * 0.4);
        gl_FragColor = vec4(col, alpha);
      }
    `;

    const dialRings: { group: THREE.Group; speed: number }[] = [];
    const dialMaterials: THREE.ShaderMaterial[] = [];

    DIAL_DATA.forEach((d) => {
      const group = new THREE.Group();
      const geo = new THREE.TorusGeometry(
        d.r,
        d.tube,
        isDesktop ? 16 : 8,
        isDesktop ? 120 : 60,
      );
      disposables.push(geo);

      const mat = new THREE.ShaderMaterial({
        vertexShader: dialVertexShader,
        fragmentShader: dialFragmentShader,
        uniforms: {
          uColor: { value: new THREE.Color(0.78, 0.66, 0.3) },
          uOpacity: { value: d.opacity },
          uTime: { value: 0 },
          uTicks: { value: d.ticks },
        },
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      disposables.push(mat);

      const mesh = new THREE.Mesh(geo, mat);
      group.add(mesh);
      group.position.z = d.z;

      dialRings.push({ group, speed: d.speed });
      dialMaterials.push(mat);
      gyroGroup.add(group);
    });

    // Lock bolts — positioned around outer ring
    const boltCount = isDesktop ? 6 : 4;
    for (let i = 0; i < boltCount; i++) {
      const angle = (i / boltCount) * Math.PI * 2;
      const boltGeo = new THREE.CylinderGeometry(
        0.055,
        0.055,
        0.12,
        isDesktop ? 8 : 6,
      );
      disposables.push(boltGeo);
      const boltMat = new THREE.MeshStandardMaterial({
        color: 0xa08030,
        metalness: 0.9,
        roughness: 0.2,
        emissive: 0xc9a84c,
        emissiveIntensity: 0.02,
      });
      disposables.push(boltMat);
      const bolt = new THREE.Mesh(boltGeo, boltMat);
      bolt.position.x = Math.cos(angle) * 3.5;
      bolt.position.y = Math.sin(angle) * 3.5;
      bolt.position.z = 0.06;
      bolt.rotation.x = Math.PI / 2;
      gyroGroup.add(bolt);
    }

    // -----------------------------------------------------------------------
    // Keyhole core (SDF + FBM shader — matching landing exactly)
    // -----------------------------------------------------------------------
    const keyholeGeo = new THREE.PlaneGeometry(2.0, 2.0);
    disposables.push(keyholeGeo);

    const keyholeOctaves = isDesktop ? 3 : 2;
    const keyholeMat = new THREE.ShaderMaterial({
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform float uIntensity;
        varying vec2 vUv;

        vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
        vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
        vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

        float snoise(vec3 v) {
          const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
          const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
          vec3 i = floor(v + dot(v, C.yyy));
          vec3 x0 = v - i + dot(i, C.xxx);
          vec3 g = step(x0.yzx, x0.xyz);
          vec3 l = 1.0 - g;
          vec3 i1 = min(g.xyz, l.zxy);
          vec3 i2 = max(g.xyz, l.zxy);
          vec3 x1 = x0 - i1 + C.xxx;
          vec3 x2 = x0 - i2 + C.yyy;
          vec3 x3 = x0 - D.yyy;
          i = mod289(i);
          vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));
          float n_ = 0.142857142857;
          vec3 ns = n_ * D.wyz - D.xzx;
          vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
          vec4 x_ = floor(j * ns.z);
          vec4 y_ = floor(j - 7.0 * x_);
          vec4 x = x_ * ns.x + ns.yyyy;
          vec4 y = y_ * ns.x + ns.yyyy;
          vec4 h = 1.0 - abs(x) - abs(y);
          vec4 b0 = vec4(x.xy, y.xy);
          vec4 b1 = vec4(x.zw, y.zw);
          vec4 s0 = floor(b0) * 2.0 + 1.0;
          vec4 s1 = floor(b1) * 2.0 + 1.0;
          vec4 sh = -step(h, vec4(0.0));
          vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
          vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
          vec3 p0 = vec3(a0.xy, h.x);
          vec3 p1 = vec3(a0.zw, h.y);
          vec3 p2 = vec3(a1.xy, h.z);
          vec3 p3 = vec3(a1.zw, h.w);
          vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
          p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
          vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
          m = m * m;
          return 42.0 * dot(m * m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
        }

        float fbm(vec3 p) {
          float v = 0.0, a = 0.5;
          for (int i = 0; i < ${keyholeOctaves}; i++) {
            v += a * snoise(p);
            p *= 2.0;
            a *= 0.5;
          }
          return v;
        }

        void main() {
          vec2 uv = (vUv - 0.5) * 2.0;

          // Keyhole SDF: octagonal diamond head + ward (matching landing)
          vec2 hp = uv - vec2(0.0, 0.12);
          float diamond = max(abs(hp.x) + abs(hp.y) - 0.24, max(abs(hp.x) - 0.19, abs(hp.y) - 0.19));
          float ward = max(abs(uv.x) - 0.07, max(-(uv.y + 0.38), uv.y - 0.02));
          float d = min(diamond, ward);

          // FBM energy (matching landing)
          float t1 = uTime * 0.15;
          float t2 = uTime * 0.23;
          vec3 samplePos = vec3(uv * 3.0, uTime * 0.12);
          float n = fbm(samplePos + vec3(t1, t2 * 0.7, -t1 * 0.5));
          n = n * 0.5 + 0.5;

          // Color: deep amber -> gold -> bright gold (matching landing)
          vec3 deepAmber = vec3(0.30, 0.18, 0.05);
          vec3 gold = vec3(0.78, 0.66, 0.30);
          vec3 brightGold = vec3(1.0, 0.92, 0.65);
          vec3 col = mix(deepAmber, gold, smoothstep(0.2, 0.5, n));
          col = mix(col, brightGold, smoothstep(0.55, 0.85, n));

          // Inner/halo/alpha (matching landing)
          float inner = 1.0 - smoothstep(-0.01, 0.02, d);
          float halo = exp(-max(d, 0.0) * 5.0) * 0.6;
          float pulse = 0.8 + 0.2 * sin(uTime * 0.2);
          col *= 1.8 * pulse * uIntensity;
          float alpha = (inner * 0.85 + halo * 0.35) * pulse;

          if (alpha < 0.001) discard;
          gl_FragColor = vec4(col * (inner + halo * 0.4), alpha);
        }
      `,
      uniforms: {
        uTime: { value: 0 },
        uIntensity: { value: 1.0 },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    disposables.push(keyholeMat);

    const keyholeQuad = new THREE.Mesh(keyholeGeo, keyholeMat);
    gyroGroup.add(keyholeQuad);

    scene.add(gyroGroup);

    // -----------------------------------------------------------------------
    // Far particles (SDF shapes — angular NieR-style, matching landing)
    // -----------------------------------------------------------------------
    const farCount = isDesktop ? 2800 : 1200;
    const farGeo = new THREE.BufferGeometry();
    const farPos = new Float32Array(farCount * 3);
    const farCol = new Float32Array(farCount * 3);
    const farShapes = new Float32Array(farCount);
    const farRotations = new Float32Array(farCount);

    for (let i = 0; i < farCount; i++) {
      const i3 = i * 3;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 10 + Math.random() * 30;
      farPos[i3] = r * Math.sin(phi) * Math.cos(theta);
      farPos[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      farPos[i3 + 2] = r * Math.cos(phi) - 12; // Z offset matching landing

      // Brightness-based color (matching landing)
      const b = 0.15 + Math.random() * 0.45;
      const warm = Math.random() > 0.55;
      farCol[i3] = warm ? b * 0.78 : b * 0.22;
      farCol[i3 + 1] = warm ? b * 0.6 : b * 0.2;
      farCol[i3 + 2] = warm ? b * 0.22 : b * 0.2;

      const rs = Math.random();
      farShapes[i] = rs < 0.15 ? 0.0 : 3.0; // 15% diamond, 85% key
      farRotations[i] = Math.random() * Math.PI * 2;
    }

    farGeo.setAttribute("position", new THREE.BufferAttribute(farPos, 3));
    farGeo.setAttribute("color", new THREE.BufferAttribute(farCol, 3));
    farGeo.setAttribute("aShape", new THREE.BufferAttribute(farShapes, 1));
    farGeo.setAttribute(
      "aRotation",
      new THREE.BufferAttribute(farRotations, 1),
    );
    disposables.push(farGeo);

    const farMat = new THREE.ShaderMaterial({
      uniforms: {
        uPixelRatio: { value: renderer.getPixelRatio() },
        uTime: { value: 0 },
        uSize: { value: 0.055 },
        uOpacity: { value: 0.65 },
      },
      vertexShader: /* glsl */ `
        attribute float aShape;
        attribute float aRotation;
        uniform float uPixelRatio;
        uniform float uSize;
        uniform float uTime;
        varying vec3 vColor;
        varying float vShape;
        varying float vRotation;
        void main() {
          vColor = color;
          vShape = aShape;
          vRotation = aRotation + uTime * 0.08;
          float shapeMul = aShape > 2.5 ? 3.5 : aShape > 1.5 ? 2.2 : aShape > 0.5 ? 2.0 : 1.5;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = uSize * shapeMul * uPixelRatio * (300.0 / -mvPosition.z);
          gl_PointSize = clamp(gl_PointSize, 1.0, 40.0);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uOpacity;
        varying vec3 vColor;
        varying float vShape;
        varying float vRotation;

        // NieR-style angular shapes — no circles, all flat geometric
        float sdDiamond(vec2 p) {
          return abs(p.x) + abs(p.y) - 0.28;
        }
        float sdCross(vec2 p) {
          vec2 a = abs(p);
          float h = max(a.x - 0.05, a.y - 0.30);
          float v = max(a.x - 0.30, a.y - 0.05);
          return min(h, v);
        }
        float sdDash(vec2 p) {
          return max(abs(p.x) - 0.32, abs(p.y) - 0.04);
        }
        float sdKey(vec2 p) {
          // Angular octagonal head
          vec2 hp = p - vec2(0.0, 0.18);
          float head = max(abs(hp.x) + abs(hp.y) - 0.15, max(abs(hp.x) - 0.12, abs(hp.y) - 0.12));
          // Hollow center
          float headInner = max(abs(hp.x) + abs(hp.y) - 0.08, max(abs(hp.x) - 0.06, abs(hp.y) - 0.06));
          head = max(head, -headInner);
          // Blocky shaft
          float shaft = max(abs(p.x) - 0.03, max(-(p.y + 0.35), p.y - 0.06));
          // Stepped pixel wards
          float w1 = max(abs(p.x - 0.055) - 0.04, max(-(p.y + 0.32), p.y + 0.18));
          float w2 = max(abs(p.x - 0.045) - 0.03, max(-(p.y + 0.24), p.y + 0.10));
          return min(head, min(shaft, min(w1, w2)));
        }
        void main() {
          vec2 p = gl_PointCoord - vec2(0.5);
          float c = cos(vRotation), s = sin(vRotation);
          p = vec2(c * p.x + s * p.y, -s * p.x + c * p.y);
          float d;
          if (vShape < 0.5) {
            d = sdDiamond(p);
          } else if (vShape < 1.5) {
            d = sdCross(p);
          } else if (vShape < 2.5) {
            d = sdDash(p);
          } else {
            d = sdKey(p);
          }
          float alpha = smoothstep(0.02, -0.02, d) * uOpacity;
          if (alpha < 0.001) discard;
          gl_FragColor = vec4(vColor, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      vertexColors: true,
    });
    disposables.push(farMat);

    const farParticles = new THREE.Points(farGeo, farMat);
    scene.add(farParticles);

    // -----------------------------------------------------------------------
    // Near particles (desktop only — cylindrical distribution, matching landing)
    // -----------------------------------------------------------------------
    let nearParticles: THREE.Points | null = null;

    if (isDesktop) {
      const nearCount = 600;
      const nearGeo = new THREE.BufferGeometry();
      const nearPos = new Float32Array(nearCount * 3);

      for (let i = 0; i < nearCount; i++) {
        const i3 = i * 3;
        const theta = Math.random() * Math.PI * 2;
        const r = 3 + Math.random() * 7;
        nearPos[i3] = r * Math.cos(theta);
        nearPos[i3 + 1] = r * Math.sin(theta);
        nearPos[i3 + 2] = 6 + Math.random() * 10;
      }
      nearGeo.setAttribute("position", new THREE.BufferAttribute(nearPos, 3));
      disposables.push(nearGeo);

      const nearMat = new THREE.PointsMaterial({
        color: 0xc9a84c,
        size: 0.04,
        transparent: true,
        opacity: 0.4,
        sizeAttenuation: true,
      });
      disposables.push(nearMat);

      nearParticles = new THREE.Points(nearGeo, nearMat);
      scene.add(nearParticles);
    }

    // -----------------------------------------------------------------------
    // Dust motes (GPU-driven, matching landing)
    // -----------------------------------------------------------------------
    const dustCount = isDesktop ? 120 : 40;
    const dustGeo = new THREE.BufferGeometry();
    const dustPositions = new Float32Array(dustCount * 3);
    const dustSizes = new Float32Array(dustCount);
    const dustSeeds = new Float32Array(dustCount);
    const dustShapes = new Float32Array(dustCount);

    for (let i = 0; i < dustCount; i++) {
      const i3 = i * 3;
      dustPositions[i3] = (Math.random() - 0.5) * 30;
      dustPositions[i3 + 1] = (Math.random() - 0.5) * 20;
      dustPositions[i3 + 2] = (Math.random() - 0.5) * 40 - 10;
      dustSizes[i] = 2.0 + Math.random() * 4.0;
      dustSeeds[i] = Math.random();
      const ds = Math.random();
      dustShapes[i] = ds < 0.15 ? 0.0 : 3.0; // 15% diamond, 85% key
    }

    dustGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(dustPositions, 3),
    );
    dustGeo.setAttribute("aSize", new THREE.BufferAttribute(dustSizes, 1));
    dustGeo.setAttribute("aSeed", new THREE.BufferAttribute(dustSeeds, 1));
    dustGeo.setAttribute("aShape", new THREE.BufferAttribute(dustShapes, 1));
    disposables.push(dustGeo);

    const dustMat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0.78, 0.66, 0.3) },
        uPixelRatio: { value: renderer.getPixelRatio() },
        uTime: { value: 0 },
      },
      vertexShader: /* glsl */ `
        attribute float aSize;
        attribute float aSeed;
        attribute float aShape;
        uniform float uPixelRatio;
        uniform float uTime;
        varying float vShape;
        varying float vAngle;
        void main() {
          vShape = aShape;
          vAngle = aSeed * 6.283;
          float seed = aSeed;
          float t = uTime;
          vec3 drift;
          drift.x = sin(t * 0.15 + seed * 62.7) * 0.8 + cos(t * 0.08 + seed * 41.3) * 0.5;
          drift.y = sin(t * 0.12 + seed * 78.4) * 0.6 + cos(t * 0.06 + seed * 23.1) * 0.3;
          drift.z = sin(t * 0.10 + seed * 95.2) * 0.4;
          vec3 pos = position + drift;
          float shapeMul = aShape > 2.5 ? 1.8 : aShape > 1.5 ? 1.2 : aShape > 0.5 ? 1.0 : 0.7;
          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_PointSize = aSize * shapeMul * uPixelRatio * (300.0 / -mvPosition.z);
          gl_PointSize = clamp(gl_PointSize, 1.0, 45.0);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        varying float vShape;
        varying float vAngle;

        // NieR-style angular shapes
        float sdDiamond(vec2 p) {
          return abs(p.x) + abs(p.y) - 0.28;
        }
        float sdCross(vec2 p) {
          vec2 a = abs(p);
          return min(max(a.x - 0.05, a.y - 0.30), max(a.x - 0.30, a.y - 0.05));
        }
        float sdDash(vec2 p) {
          return max(abs(p.x) - 0.32, abs(p.y) - 0.04);
        }
        float sdKey(vec2 p) {
          vec2 hp = p - vec2(0.0, 0.18);
          float head = max(abs(hp.x) + abs(hp.y) - 0.15, max(abs(hp.x) - 0.12, abs(hp.y) - 0.12));
          float headInner = max(abs(hp.x) + abs(hp.y) - 0.08, max(abs(hp.x) - 0.06, abs(hp.y) - 0.06));
          head = max(head, -headInner);
          float shaft = max(abs(p.x) - 0.03, max(-(p.y + 0.35), p.y - 0.06));
          float w1 = max(abs(p.x - 0.055) - 0.04, max(-(p.y + 0.32), p.y + 0.18));
          float w2 = max(abs(p.x - 0.045) - 0.03, max(-(p.y + 0.24), p.y + 0.10));
          return min(head, min(shaft, min(w1, w2)));
        }
        void main() {
          vec2 p = gl_PointCoord - vec2(0.5);
          float c = cos(vAngle), s = sin(vAngle);
          p = vec2(c * p.x + s * p.y, -s * p.x + c * p.y);
          float d;
          if (vShape < 0.5) {
            d = sdDiamond(p);
          } else if (vShape < 1.5) {
            d = sdCross(p);
          } else if (vShape < 2.5) {
            d = sdDash(p);
          } else {
            d = sdKey(p);
          }
          float alpha = smoothstep(0.02, -0.02, d) * 0.11;
          if (alpha < 0.001) discard;
          gl_FragColor = vec4(uColor, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    disposables.push(dustMat);

    const dustMotes = new THREE.Points(dustGeo, dustMat);
    scene.add(dustMotes);

    // -----------------------------------------------------------------------
    // Post-processing (matching landing)
    // -----------------------------------------------------------------------
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));

    const bloom = new UnrealBloomPass(
      new THREE.Vector2(w, h),
      isDesktop ? 0.8 : 0.6, // strength
      0.4, // radius
      0.85, // threshold
    );
    composer.addPass(bloom);

    // Cinematic pass: vignette + chromatic aberration + film grain (matching landing)
    const caStrength = isDesktop ? "0.003" : "0.002";
    const cinematicPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        darkness: { value: 1.3 },
        offset: { value: 1.0 },
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(w, h) },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D tDiffuse;
        uniform float darkness;
        uniform float offset;
        uniform float uTime;
        uniform vec2 uResolution;
        varying vec2 vUv;
        void main() {
          vec2 uv = vUv;
          vec2 center = uv - 0.5;
          float dist = length(center);

          // Chromatic aberration along center direction (matching landing)
          float caStrength = ${caStrength} * dist;
          float r = texture2D(tDiffuse, uv + center * caStrength).r;
          float g = texture2D(tDiffuse, uv).g;
          float b = texture2D(tDiffuse, uv - center * caStrength).b;
          vec4 texel = vec4(r, g, b, 1.0);

          // Film grain (matching landing)
          vec2 seed = uv * uResolution + uTime * 1000.0;
          float grain = fract(sin(dot(seed, vec2(12.9898, 78.233))) * 43758.5453);
          texel.rgb += (grain - 0.5) * 0.035;

          // Vignette (matching landing)
          vec2 vig = (vUv - 0.5) * 2.0;
          float vigFactor = 1.0 - smoothstep(offset - 0.5, offset + 0.5, length(vig));
          texel.rgb *= mix(1.0, vigFactor, darkness * 0.5);

          gl_FragColor = texel;
        }
      `,
    });
    composer.addPass(cinematicPass);

    // No OutputPass — matching landing page (r128) which doesn't apply
    // sRGB output encoding in the composer. This gives the darker, muted look.

    // -----------------------------------------------------------------------
    // Mouse tracking
    // -----------------------------------------------------------------------
    let mouseX = 0;
    let mouseY = 0;
    const onMouseMove = (e: MouseEvent) => {
      mouseX = (e.clientX / w - 0.5) * 2;
      mouseY = (e.clientY / h - 0.5) * 2;
    };
    if (isDesktop) {
      window.addEventListener("mousemove", onMouseMove);
    }

    // -----------------------------------------------------------------------
    // State ref
    // -----------------------------------------------------------------------
    const state = {
      renderer,
      composer,
      bloom,
      camera,
      scene,
      ringGroup,
      ringMeshes,
      ringMaterials,
      gyroGroup,
      dialRings,
      dialMaterials,
      keyholeQuad,
      keyholeMat,
      farParticles,
      farMat,
      nearParticles,
      dustMotes,
      dustMat,
      lantern,
      cinematicPass,
      frameId: 0,
      frame: 0,
      lastRender: 0,
      targetX: 0,
      targetY: 0,
      mouseX: 0,
      mouseY: 0,
      reducedMotion,
      disposables,
      isDesktop,
    };
    stateRef.current = state;

    // -----------------------------------------------------------------------
    // Animation loop (frame-counter based, matching landing)
    // -----------------------------------------------------------------------
    const targetFPS = isDesktop ? 60 : 30;
    const frameInterval = 1000 / targetFPS;

    const animate = (now: number) => {
      state.frameId = requestAnimationFrame(animate);

      // FPS throttle
      if (now - state.lastRender < frameInterval) return;
      state.lastRender = now;
      state.frame++;

      const t = state.frame * (isDesktop ? 0.004 : 0.008);
      const prog = progressRef.current;

      // Mouse lerp (matching landing)
      state.targetX += (mouseX - state.targetX) * 0.038;
      state.targetY += (mouseY - state.targetY) * 0.038;

      // Camera position (matching landing)
      camera.position.x = state.targetX * 0.55;
      camera.position.y = state.targetY * 0.35;
      camera.position.z = 18 - prog * 2; // progress-based (webenv has no scroll)
      camera.lookAt(0, 0, 0);

      // Bloom intensity ramp with progress
      bloom.strength = (isDesktop ? 0.8 : 0.6) + prog * 0.4;

      // Vault mechanism — gyroGroup rotation (matching landing)
      gyroGroup.rotation.y = t * 0.04;
      gyroGroup.rotation.x = Math.sin(t * 0.08) * 0.06 + state.targetY * 0.04;
      gyroGroup.rotation.z = Math.cos(t * 0.05) * 0.02;

      // Dial rotation (matching landing)
      dialRings.forEach((d) => {
        d.group.rotation.z = t * d.speed;
      });

      // Ring group rotation + per-ring wobble (matching landing)
      ringGroup.rotation.x = state.targetY * 0.025 + Math.sin(t * 0.07) * 0.018;
      ringGroup.rotation.y = state.targetX * 0.025 + t * 0.06;
      ringMeshes.forEach(({ mesh, speed, baseTilt }, idx) => {
        mesh.rotation.z += speed;
        mesh.rotation.x = baseTilt + Math.sin(t * 0.15 + idx * 0.7) * 0.008;
      });

      // Far particles rotation (matching landing)
      farParticles.rotation.y = t * 0.006;

      // Near particles parallax (matching landing)
      if (nearParticles) {
        nearParticles.position.x = -state.targetX * 0.6;
        nearParticles.position.y = state.targetY * 0.4;
      }

      // Enhanced lantern flicker — two harmonics + random jitter (matching landing)
      lantern.intensity =
        4.0 +
        Math.sin(t * 1.8) * 0.5 +
        Math.sin(t * 3.7) * 0.25 +
        (Math.random() - 0.5) * 0.15;

      // Update shader uniforms (matching landing)
      ringMaterials.forEach((mat) => {
        const uTime = mat.uniforms.uTime;
        if (uTime) uTime.value = t;
      });
      dialMaterials.forEach((m) => {
        const uTime = m.uniforms.uTime;
        if (uTime) uTime.value = t;
      });
      const khTime = keyholeMat.uniforms.uTime;
      if (khTime) khTime.value = t;
      const khIntensity = keyholeMat.uniforms.uIntensity;
      if (khIntensity) khIntensity.value = 1.0 + prog * 0.5;
      const dustTime = dustMat.uniforms.uTime;
      if (dustTime) dustTime.value = t;
      const farTime = farMat.uniforms.uTime;
      if (farTime) farTime.value = t;
      const cinTime = cinematicPass.uniforms.uTime;
      if (cinTime) cinTime.value = t;

      composer.render();
    };

    if (reducedMotion) {
      composer.render();
    } else {
      requestAnimationFrame(animate);
    }

    // -----------------------------------------------------------------------
    // Resize handler
    // -----------------------------------------------------------------------
    const onResize = () => {
      const nw = container.clientWidth;
      const nh = container.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
      composer.setSize(nw, nh);
      const uResolution = cinematicPass.uniforms.uResolution;
      if (uResolution) {
        (uResolution.value as THREE.Vector2).set(nw, nh);
      }
    };
    window.addEventListener("resize", onResize);

    // -----------------------------------------------------------------------
    // Cleanup
    // -----------------------------------------------------------------------
    return () => {
      if (isDesktop) {
        window.removeEventListener("mousemove", onMouseMove);
      }
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(state.frameId);

      disposables.forEach((d) => {
        d.dispose();
      });

      bloom.dispose();
      composer.dispose();
      renderer.dispose();

      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      stateRef.current = null;
    };
  }, []); // Mount once

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: "100%", height: "100%", overflow: "hidden" }}
    />
  );
}

export default VaultScene;
